// src/a2hs.jsx — Phase 21b Add-to-Home-Screen install prompt.
// Renders a dismissable pill below the UpdateBanner; on Chromium fires the
// native prompt via triggerInstall(); on iOS opens an instruction sheet.

import React from 'react';
import { isStandalone, triggerInstall } from './pwa.jsx';
import { LL, LLClose } from './tokens.jsx';
import { LLSheet } from './sheets.jsx';
import { useApp } from './store.jsx';
import { backupNudgeDue } from './sync.js';
import { missingTMs } from './progression.js';

const A2HS_DISMISSED_KEY = 'liftlog.a2hsDismissed';

function detectPlatform() {
  try {
    const ua = (navigator && navigator.userAgent) || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !/Windows/.test(ua);
    const isAndroid = /Android/.test(ua);
    const hasDeferred = !!window.__deferredInstallPrompt;
    return {
      isIOS, isAndroid,
      chromium: hasDeferred,
      mobile: isIOS || isAndroid,
    };
  } catch (_) {
    return { isIOS: false, isAndroid: false, chromium: false, mobile: false };
  }
}

function isDismissed() {
  try {
    return localStorage.getItem(A2HS_DISMISSED_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

function markDismissed() {
  try { localStorage.setItem(A2HS_DISMISSED_KEY, 'true'); } catch (_) {}
}

// iOS / other-browser instructions, in the shared bottom sheet.
function A2HSInstructionsSheet({ open = true, onClose }) {
  return (
    <LLSheet open={open} onClose={onClose} title="Add to Home Screen" subtitle="Install Lift Log">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Step num="1" text="Tap the Share icon in your browser's toolbar"><ShareIcon /></Step>
        <Step num="2" text='Scroll and tap "Add to Home Screen"'><PlusBoxIcon /></Step>
        <Step num="3" text='Tap "Add" to confirm. Lift Log appears on your home screen'><HomeIcon /></Step>
      </div>
      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: LL.r.md,
        background: LL.accentSoft, border: `1px solid color-mix(in oklab, ${LL.accent} 30%, transparent)`,
        fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.5,
      }}>
        Opens full screen with no browser bars, works offline, and vibrates when your rest is up.
      </div>
    </LLSheet>
  );
}

function Step({ num, text, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 10, borderRadius: LL.r.md,
      background: LL.surface2, border: `1px solid ${LL.line}`,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: LL.r.sm,
        background: LL.accentSoft, color: LL.accentText,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>{children}</div>
      <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fgDim, fontVariantNumeric: 'tabular-nums' }}>{num}</span>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg, lineHeight: 1.4 }}>{text}</span>
      </div>
    </div>
  );
}

function ShareIcon({ color = 'currentColor' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <polyline points="7 8 12 3 17 8" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}
function PlusBoxIcon({ color = 'currentColor' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}
function HomeIcon({ color = 'currentColor' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10l9-7 9 7v10a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2V10z" />
    </svg>
  );
}

// Does Home already show its one system banner (training maxes / backup)?
function homeBannerDue(state) {
  try {
    const prog = (state.programs || []).find(p => p.id === state.activeProgramId);
    if (prog && missingTMs(prog).length) return true;
    return !!backupNudgeDue(state);
  } catch { return false; }
}

function A2HSPrompt() {
  const { state, route } = useApp();
  const [visible, setVisible] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [platform, setPlatform] = React.useState(() => detectPlatform());

  React.useEffect(() => {
    const gatesBlock = () => {
      if (isDismissed()) return true;
      if (typeof isStandalone === 'function' && isStandalone()) return true;
      return false;
    };
    if (gatesBlock()) return;

    // Detect platform once at mount. iOS Safari NEVER fires
    // beforeinstallprompt, so skip the chromium polling on iOS entirely —
    // any truthy __deferredInstallPrompt on an iOS UA is suspect anyway.
    const initialPlatform = detectPlatform();

    // Chromium path — show as soon as beforeinstallprompt captures.
    // Skip entirely on iOS UA (no native install; use share-sheet path).
    const pollForDeferred = !initialPlatform.isIOS ? setInterval(() => {
      if (gatesBlock()) {
        clearInterval(pollForDeferred);
        if (iosTimer) clearTimeout(iosTimer);
        return;
      }
      const p = detectPlatform();
      if (p.chromium || (window.__deferredInstallPrompt)) {
        setPlatform({ ...p, chromium: true });
        setVisible(true);
        clearInterval(pollForDeferred);
        if (iosTimer) clearTimeout(iosTimer);
      }
    }, 500) : null;

    // iOS / mobile-other path — show after 2s boot delay
    let iosTimer = null;
    if (initialPlatform.isIOS || initialPlatform.isAndroid) {
      iosTimer = setTimeout(() => {
        if (gatesBlock()) return;
        setPlatform(detectPlatform());
        setVisible(true);
      }, 2000);
    }

    return () => {
      if (pollForDeferred) clearInterval(pollForDeferred);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const onDismiss = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    markDismissed();
    setVisible(false);
  };

  const onInstallTap = async () => {
    if (platform.chromium) {
      try {
        const result = await (typeof triggerInstall === 'function'
          ? triggerInstall()
          : Promise.resolve({ outcome: 'unavailable' }));
        if (result && result.outcome === 'accepted') {
          markDismissed();
          setVisible(false);
          try {
            const { actions } = (window.__ll_appActions && window.__ll_appActions()) || {};
            actions && actions.toast && actions.toast('Added to home screen');
          } catch (_) {}
        }
      } catch (_) {
        // Swallow — banner stays; user can retry or dismiss
      }
    } else if (platform.isIOS) {
      setSheetOpen(true);
    } else {
      // Desktop / other — show instructions sheet with generic copy
      setSheetOpen(true);
    }
  };

  // Ask only after the app has proven itself (a finished workout), and never
  // over the workout flow or while the minimized-workout bar is showing.
  const hasFinished = (state.sessions || []).some(x => x.completedAt);
  const busyRoute = ['welcome', 'active-workout', 'workout-summary', 'ai-coach'].includes(route.name) || route.name.startsWith('settings/')
    // Home shows at most one system banner (its own TM / backup row); don't stack the install prompt on it.
    || (route.name === 'home' && (homeBannerDue(state)));
  // The update banner uses the same spot above the tab bar: it wins.
  const showPill = visible && hasFinished && !busyRoute && !state.activeSessionId && !window.__liftlogUpdateReady;
  if (!showPill && !sheetOpen) return null;

  // Platform-specific copy
  let title, body, cta;
  if (platform.chromium) {
    title = 'Install Lift Log';
    body = 'Launch from home screen — works offline';
    cta = 'Install';
  } else if (platform.isIOS) {
    title = 'Add to Home Screen';
    body = 'Tap Share → Add to Home Screen';
    cta = 'Show me';
  } else {
    title = 'Install for quick access';
    body = "Use your browser's menu to add this page";
    cta = '';
  }

  // Same card as the update banner (pwa.jsx): surface2, accent hairline, md shadow.
  const btn = {
    height: 36, borderRadius: LL.r.sm, border: 'none', cursor: 'pointer', flexShrink: 0,
    fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600,
  };
  return (
    <>
      {showPill && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)',
            left: 12,
            right: 12,
            zIndex: 58,
            padding: '8px 8px 8px 14px',
            borderRadius: LL.r.md,
            background: LL.surface2,
            border: `1px solid color-mix(in oklab, ${LL.accent} 40%, ${LL.line})`,
            color: LL.fg,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontFamily: LL.ui,
            fontSize: LL.fs.meta,
            boxShadow: LL.shadow.md,
            animation: `toastIn ${LL.motion.base}ms ${LL.ease}`,
          }}
        >
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
            <span style={{ display: 'block', color: LL.accentText, fontWeight: 700 }}>{title}</span>
            <span style={{ display: 'block', color: LL.fgMute, fontSize: LL.fs.sm }}>{body}</span>
          </span>
          {cta && (
            <button type="button" onClick={onInstallTap} style={{ ...btn, padding: '0 14px', background: LL.accent, color: LL.accentInk }}>
              {cta}
            </button>
          )}
          <LLClose onClick={onDismiss} label="Dismiss install prompt" style={{ margin: '-6px -4px -6px -6px' }} />
        </div>
      )}
      <A2HSInstructionsSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

export { A2HSPrompt, A2HSInstructionsSheet };
